import { motion } from "framer-motion";
import Reveal, { EASE } from "@/components/Reveal";
import { PRODUCTS, SHOP_URL } from "@/data/site";
import { ArrowUpRight } from "lucide-react";

export default function Products() {
  return (
    <section id="cleenvo" data-testid="cleenvo-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-green/10 blur-3xl" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <Reveal className="max-w-2xl mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-greenDark" data-testid="cleenvo-overline">Cleenvo — Our Own Brand</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight text-slate-900" data-testid="cleenvo-heading">
            The Cleenvo <span className="font-serif italic font-semibold text-brand-blue">Collection</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600" data-testid="cleenvo-subtext">
            Powerful, safe and refreshing cleaning essentials for homes and businesses —
            available in 500 ml, 1 L and 5 L packs. Sold exclusively on{" "}
            <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-blue underline decoration-brand-green decoration-2 underline-offset-4" data-testid="cleenvo-shop-link">
              officiaa.com
            </a>
            .
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 gap-y-12" data-testid="product-grid">
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: EASE }}
              className="group flex flex-col"
            >
              <div className="spotlight-frame relative overflow-hidden rounded-tl-lg rounded-br-lg rounded-tr-[3rem] rounded-bl-[3rem] border border-slate-200 transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_24px_50px_-24px_rgba(18,58,102,0.35)]">
                {p.tag && (
                  <span className="absolute left-4 top-4 z-10 rounded-full bg-brand-blue px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white" data-testid={`product-tag-${p.id}`}>
                    {p.tag}
                  </span>
                )}
                <img
                  src={p.img}
                  alt={`Cleenvo ${p.name}`}
                  loading="lazy"
                  className="h-60 w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                  data-testid={`product-image-${p.id}`}
                />
              </div>
              <h3 className="mt-5 font-display text-lg font-extrabold tracking-tight text-slate-900" data-testid={`product-name-${p.id}`}>
                {p.name}
              </h3>
              <p className="mt-1 text-sm text-slate-500" data-testid={`product-variant-${p.id}`}>{p.variant}</p>
              <div className="mt-3 flex items-baseline gap-2.5" data-testid={`product-price-${p.id}`}>
                <span className="text-xl font-extrabold text-brand-green">₹{p.price}</span>
                <span className="text-sm text-slate-400 line-through">₹{p.mrp}</span>
                <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[11px] font-bold text-brand-greenDark">
                  {Math.round(((p.mrp - p.price) / p.mrp) * 100)}% off
                </span>
              </div>
              <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`product-buy-${p.id}`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-green py-3 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-greenDark hover:-translate-y-0.5"
              >
                Buy Now
                <ArrowUpRight size={15} />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
